<template>
  <div :class="gridClass">
    <label class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <div class="flex">
      <!-- Préfixe France -->
      <div class="flex items-center px-3 py-2 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-600 font-medium">
        🇫🇷 +33
      </div>
      
      <!-- Champ téléphone -->
      <input
        :value="modelValue"
        @input="onInput"
        @blur="onBlur"
        type="tel"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        class="flex-1 px-3 py-2 border rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
        :class="inputClasses"
        inputmode="numeric"
        autocomplete="tel"
      />
    </div>
    
    <!-- Message d'erreur -->
    <div v-if="errorMessage" class="mt-1 text-sm text-red-600">
      {{ errorMessage }}
    </div>
    
    <!-- Indicateur de caractères -->
    <div v-if="modelValue" class="mt-1 text-xs text-gray-500">
      {{ modelValue.length }}/10 caractères
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { PhoneFieldProps } from '@/types/sellerRegister';

const props = defineProps<PhoneFieldProps>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'validation-changed': [isValid: boolean];
}>();
// État local pour la validation
const errorMessage = ref('');
const hasBeenTouched = ref(false);

// Classes CSS pour l'input
const inputClasses = computed(() => {
  const baseClasses = 'focus:outline-none';
  
  if (errorMessage.value) {
    return `${baseClasses} border-red-500`;
  } else if (props.modelValue && isValidPhone.value) {
    return `${baseClasses} border-green-500`;
  } else {
    return `${baseClasses} border-gray-300`;
  }
});

// Validation du numéro de téléphone français
const isValidPhone = computed(() => {
  if (!props.modelValue) return false;
  
  const phoneRegex = /^0[1-9](\d{8})$/;
  
  switch (props.validationType) {
    case 'phone':
    case 'mobile':
      // Format strict français : 10 chiffres commençant par 0
      return phoneRegex.test(props.modelValue);
    
    case 'fax':
      // Fax : format français OU vide (optionnel)
      if (props.modelValue === '') return true; // Vide autorisé
      return phoneRegex.test(props.modelValue);
    
    default:
      // Par défaut : validation stricte
      return phoneRegex.test(props.modelValue);
  }
});

// Validation complète avec messages d'erreur
const validatePhone = () => {
  if (!props.modelValue) {

    if (props.validationType === 'fax') {
      errorMessage.value = '';
      return true;
    }
    errorMessage.value = '';
    return true;
  }
  
  if (props.modelValue.length < 10) {
    errorMessage.value = 'Le numéro doit contenir 10 chiffres';
    return false;
  }
  
  if (!isValidPhone.value) {
    errorMessage.value = 'Format invalide. Utilisez le format français (ex: 0123456789)';
    return false;
  }
  
  errorMessage.value = '';
  return true;
};

// Gérer les changements d'input
const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const inputValue = target.value;
  
  // Vérifier si l'input contient des caractères non-numériques
  if (/\D/.test(inputValue)) {
    target.value = props.modelValue;
    return;
  }

  let value = inputValue;
  
  // Bloquer à 10 chiffres maximum
  if (value.length > 10) {
    target.value = props.modelValue;
    return;
  }
  
  emit('update:modelValue', value);
  
  // Valider et émettre l'état
  const isValid = validatePhone();
  emit('validation-changed', isValid);
};

// Gérer la perte de focus
const onBlur = () => {
  hasBeenTouched.value = true;
  
  // Valider seulement si le champ a été touché ou s'il y a une valeur
  if (hasBeenTouched.value || props.modelValue) {
    const isValid = validatePhone();
    emit('validation-changed', isValid);
  }
};

// Watcher pour la validation externe
watch(() => props.modelValue, (newValue) => {
  if (hasBeenTouched.value || newValue) {
    validatePhone();
  }
}, { immediate: true });

// Watcher pour la prop error externe
watch(() => props.error, (newError) => {
  if (newError && !errorMessage.value) {
    errorMessage.value = 'Numéro de téléphone invalide';
  }
});
</script>

<style scoped>
/* Styles pour le préfixe pays */
.prefix-country {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-color: #d1d5db;
}
</style>
